import { createTheme } from '@mui/material/styles';

const brandShadow = '0 1px 0 rgba(14,15,18,.04), 0 18px 44px -28px rgba(14,15,18,.28)';

// NAPC vision-board theme: warm paper, restrained pink CTAs, blue trust accents.
export const theme = createTheme({
  palette: {
    primary: {
      main: '#ED1790',
      light: '#F45DB3',
      dark: '#B01F8E',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#306FC8',
      light: '#5E8FD7',
      dark: '#1F4F96',
      contrastText: '#ffffff',
    },
    error: {
      main: '#dc2626',
      light: '#ef4444',
      dark: '#b91c1c',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    info: {
      main: '#306FC8',
      light: '#5E8FD7',
      dark: '#1F4F96',
    },
    background: {
      default: '#FBF9F6',
      paper: '#FFF8E8',
    },
    text: {
      primary: '#0E0F12',
      secondary: '#6B6F7B',
    },
  },
  typography: {
    fontFamily: '"Source Sans 3", "Poppins", system-ui, sans-serif',
    h1: {
      fontFamily: '"Poppins", "Source Sans 3", system-ui, sans-serif',
      fontSize: 'clamp(2.6rem, 7vw, 5rem)',
      fontWeight: 700,
      lineHeight: 0.98,
      letterSpacing: '-0.04em',
    },
    h2: {
      fontFamily: '"Poppins", "Source Sans 3", system-ui, sans-serif',
      fontSize: 'clamp(2rem, 4vw, 3.25rem)',
      fontWeight: 700,
      lineHeight: 1.04,
      letterSpacing: '-0.035em',
    },
    h3: {
      fontFamily: '"Poppins", "Source Sans 3", system-ui, sans-serif',
      fontSize: 'clamp(1.75rem, 3vw, 2.6rem)',
      fontWeight: 700,
      lineHeight: 1.08,
      letterSpacing: '-0.03em',
    },
    h4: {
      fontFamily: '"Poppins", "Source Sans 3", system-ui, sans-serif',
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: 1.15,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 18,
  },
  shadows: [
    'none',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#FBF9F6',
          color: '#1B1D22',
          WebkitFontSmoothing: 'antialiased',
          textRendering: 'optimizeLegibility',
        },
        '::selection': {
          backgroundColor: 'rgba(237, 23, 144, .18)',
        },
        ':focus-visible': {
          outline: '3px solid rgba(237, 23, 144, .35)',
          outlineOffset: 3,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: 999,
          padding: '10px 18px',
          cursor: 'pointer',
          transition: 'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease, border-color 180ms ease',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          boxShadow: '0 14px 28px -18px rgba(237, 23, 144, .85)',
          '&:hover': {
            boxShadow: '0 20px 34px -20px rgba(237, 23, 144, .95)',
          },
        },
        outlined: {
          borderColor: '#E7E5EA',
          color: '#1B1D22',
          backgroundColor: 'rgba(255,255,255,.55)',
          '&:hover': {
            borderColor: '#ED1790',
            backgroundColor: 'rgba(237, 23, 144, .06)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          border: '1px solid #E7E5EA',
          backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,.92), rgba(255,248,232,.82))',
          boxShadow: brandShadow,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 22,
          border: '1px solid #E7E5EA',
          backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,.94), rgba(255,248,232,.82))',
          boxShadow: brandShadow,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontFamily: '"JetBrains Mono", ui-monospace, monospace',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '.04em',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 16,
            backgroundColor: 'rgba(255,255,255,.72)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 28,
        },
      },
    },
  },
});
